/* Owns the live-feed ring buffer of PANEL.md §11: the last 500 items, the pause
   side-buffer with its `bufferedCount`, and the per-server "new rows" counter the
   detections list raises when an event cannot be prepended. A module singleton
   read through `useSyncExternalStore`, deliberately outside React so
   `ws/useServerFeed.ts` can write to it from a socket callback without a render
   pass, and outside `lib/` so `lib/ws.ts` stays a pure transport. */

import type { BanDto, DetectionDto } from '@sentinelac/shared'
import { useSyncExternalStore } from 'react'

/** One row of the live stream. `id` is the DTO id, which is what de-duplication keys on. */
export type FeedItem =
  | { kind: 'detection'; id: string; serverId: string; at: string; detection: DetectionDto }
  | { kind: 'ban'; id: string; serverId: string; at: string; ban: BanDto }

export const FEED_CAPACITY = 500

export interface FeedState {
  items: readonly FeedItem[]
  paused: boolean
  bufferedCount: number
  /** Rows that arrived while a date filter made an in-place prepend wrong, per server. */
  newRows: Readonly<Record<string, number>>
}

const emptyState: FeedState = { items: [], paused: false, bufferedCount: 0, newRows: {} }

let state: FeedState = emptyState
/** Held out of `state` so a paused feed re-renders on the count alone, never on the rows. */
let sideBuffer: FeedItem[] = []
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getState(): FeedState {
  return state
}

/** Newest first, capped at `FEED_CAPACITY`; a repeated id is dropped rather than duplicated. */
function merge(existing: readonly FeedItem[], incoming: readonly FeedItem[]): FeedItem[] {
  const seen = new Set(existing.map((item) => item.id))
  const fresh = incoming.filter((item) => !seen.has(item.id))
  if (fresh.length === 0) return existing as FeedItem[]
  return [...fresh, ...existing].slice(0, FEED_CAPACITY)
}

export function pushFeedItem(item: FeedItem): void {
  if (state.paused) {
    // While paused the visible list must not move under the cursor, so only the
    // badge changes; the rows land when the operator resumes.
    if (sideBuffer.some((buffered) => buffered.id === item.id)) return
    sideBuffer = [item, ...sideBuffer].slice(0, FEED_CAPACITY)
    state = { ...state, bufferedCount: sideBuffer.length }
    emit()
    return
  }
  const items = merge(state.items, [item])
  if (items === state.items) return
  state = { ...state, items }
  emit()
}

/** Backfill after a `hello`: many rows at once, de-duplicated on `detections.id`. */
export function mergeFeedItems(incoming: readonly FeedItem[]): void {
  if (incoming.length === 0) return
  const items = merge(state.items, incoming)
  if (items === state.items) return
  state = { ...state, items }
  emit()
}

export function setFeedPaused(paused: boolean): void {
  if (state.paused === paused) return
  if (paused) {
    state = { ...state, paused: true }
    emit()
    return
  }
  const items = merge(state.items, sideBuffer)
  sideBuffer = []
  state = { ...state, paused: false, bufferedCount: 0, items }
  emit()
}

export function clearFeed(): void {
  sideBuffer = []
  state = { ...state, items: [], bufferedCount: 0 }
  emit()
}

/** Raised when a `detection` could not be prepended because a date filter is active. */
export function noteNewRows(serverId: string): void {
  const current = state.newRows[serverId] ?? 0
  state = { ...state, newRows: { ...state.newRows, [serverId]: current + 1 } }
  emit()
}

export function clearNewRows(serverId: string): void {
  if (state.newRows[serverId] === undefined) return
  const newRows = { ...state.newRows }
  delete newRows[serverId]
  state = { ...state, newRows }
  emit()
}

/** Test seam: the singleton would otherwise carry rows between cases. */
export function resetFeedStoreForTests(): void {
  sideBuffer = []
  state = emptyState
  emit()
}

export function useFeedState(): FeedState {
  return useSyncExternalStore(subscribe, getState, getState)
}

/** The rows for one server, which is all any mounted page ever renders. */
export function useServerFeedItems(serverId: string | null): readonly FeedItem[] {
  const { items } = useFeedState()
  if (!serverId) return []
  return items.filter((item) => item.serverId === serverId)
}

export { getState as getFeedState, subscribe as subscribeFeedStore }
